// =============================================================================
// MeuDriver — Geocoding adapter
// -----------------------------------------------------------------------------
// Provider padrão: Mapbox Geocoding API.
// Provider alternativo: Nominatim/OpenStreetMap (comentado abaixo).
//
// Por que adapter:
//   * O Nominatim (heroico, mas com termos de uso restritivos — "no heavy use",
//     atribuição obrigatória, sem keepalive estável) não é viável para o tráfego
//     que esperamos. Mapbox tem free tier de 100k requests/mês — suficiente
//     com sobra para o MVP.
//   * Centralizar o adapter aqui permite trocar de provedor sem mexer no
//     BookingFlow. Se um dia formos para Google Places, Here, ou um custom
//     próprio, basta reimplementar `geocode()`.
//
// Configuração:
//   * Definir `VITE_MAPBOX_TOKEN` no `.env.local` (token público do Mapbox).
//   * Se a env não estiver setada, o adapter loga aviso e retorna [] (degrada
//     graciosamente — o input de endereço vira "free text" e o usuário pode
//     digitar manualmente sem autocomplete).
// =============================================================================

export interface GeocodingResult {
  /** Latitude em graus decimais (WGS84). */
  lat: number
  /** Longitude em graus decimais (WGS84). */
  lon: number
  /** Texto amigável formatado: "Rua, número — Bairro — Cidade — UF". */
  displayName: string
}

interface MapboxFeature {
  id: string
  place_name: string
  text: string
  center: [number, number] // [lon, lat]
  context?: { id: string; text: string; short_code?: string }[]
  properties?: { address?: string }
  address?: string
}

interface MapboxResponse {
  type: string
  features: MapboxFeature[]
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

let warnedMissingToken = false
function ensureToken(): string | null {
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.length === 0) {
    if (!warnedMissingToken) {
      warnedMissingToken = true
      if (import.meta.env.DEV) {
        console.warn(
          '[geocoding] VITE_MAPBOX_TOKEN ausente — autocomplete de endereço desabilitado. ' +
            'Configure em .env.local para habilitar.',
        )
      }
    }
    return null
  }
  return MAPBOX_TOKEN
}

function formatMapboxFeature(f: MapboxFeature): string {
  // Mapbox `place_name` já vem formatado, ex: "Rua X, 123, Bairro Y, Cidade - UF, CEP, Brasil".
  // Para o nosso caso, mantemos `place_name` como displayName mas removemos o ", Brasil" final.
  return f.place_name.replace(/,\s*Brasil$/, '').trim()
}

/**
 * Busca sugestões de endereço para a query informada.
 *
 * Restringe a busca ao Brasil (`country=BR`), idioma português, limite de 6
 * resultados — o suficiente para um autocomplete sem poluir a UI.
 *
 * Retorna lista vazia em qualquer falha (rede, 4xx, 5xx) — não lança. O input
 * pode degradar para "free text" sem autocomplete.
 */
export async function geocode(address: string): Promise<GeocodingResult[]> {
  const trimmed = address.trim()
  if (trimmed.length < 4) return []

  const token = ensureToken()
  if (!token) return []

  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmed)}.json` +
    `?country=BR&language=pt&limit=6&types=address,place,locality,neighborhood&access_token=${token}`

  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!res.ok) return []

    const data: MapboxResponse = await res.json()
    return (data.features ?? []).map((f) => ({
      lat: f.center[1],
      lon: f.center[0],
      displayName: formatMapboxFeature(f),
    }))
  } catch {
    // Rede caiu ou JSON inválido — degrada silenciosamente.
    return []
  }
}

// -----------------------------------------------------------------------------
// FALLBACK: Nominatim/OpenStreetMap — manter como referência, NÃO usar em
// produção a menos que seja para uso muito leve / dev local. Termos de uso:
// https://operations.osmfoundation.org/policies/nominatim/
// -----------------------------------------------------------------------------
//
// export async function geocodeNominatim(address: string): Promise<GeocodingResult[]> {
//   const trimmed = address.trim()
//   if (trimmed.length < 4) return []
//   const url =
//     `https://nominatim.openstreetmap.org/search?` +
//     `q=${encodeURIComponent(trimmed)}&format=json&limit=6&countrycodes=br&addressdetails=1`
//   try {
//     const res = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } })
//     if (!res.ok) return []
//     const data: Array<{
//       lat: string
//       lon: string
//       display_name: string
//       address: Record<string, string>
//     }> = await res.json()
//     return data.map((r) => {
//       const a = r.address
//       const parts: string[] = []
//       if (a.road) parts.push(a.house_number ? `${a.road}, ${a.house_number}` : a.road)
//       if (a.suburb) parts.push(a.suburb)
//       const city = a.city ?? a.town ?? a.village
//       if (city) parts.push(city)
//       if (a.state) parts.push(a.state)
//       return {
//         lat: parseFloat(r.lat),
//         lon: parseFloat(r.lon),
//         displayName: parts.join(' — ') || r.display_name,
//       }
//     })
//   } catch {
//     return []
//   }
// }
